import React, { Component } from "react";
import PropTypes from "prop-types";

import GenericChartComponent from "../GenericChartComponent";
import { getAxisCanvas, getMouseCanvas } from "../GenericComponent";
import { getStrokeDasharrayCanvas, hexToRGBA, isDefined } from "../utils";

const normalizeStroke = (stroke, fallback) => {
  if (typeof stroke === "string") {
    return { ...fallback, color: stroke };
  }
  return {
    color: stroke?.color ?? fallback.color,
    width: stroke?.width ?? fallback.width,
    opacity: stroke?.opacity ?? fallback.opacity,
    dash: stroke?.dash ?? fallback.dash,
  };
};

const getBarWidth = (plotData, xScale, xAccessor, index, defaultWidth = 6) => {
  const left = index > 0 ? xScale(xAccessor(plotData[index - 1])) : undefined;
  const right =
    index < plotData.length - 1
      ? xScale(xAccessor(plotData[index + 1]))
      : undefined;
  const center = xScale(xAccessor(plotData[index]));

  if (isDefined(left) && isDefined(right)) {
    return Math.max(2, Math.min(12, (right - left) / 2));
  }
  if (isDefined(left)) {
    return Math.max(2, Math.min(12, center - left));
  }
  if (isDefined(right)) {
    return Math.max(2, Math.min(12, right - center));
  }
  return defaultWidth;
};

class ATRStopSeries extends Component {
  constructor(props) {
    super(props);
    this.drawOnCanvas = this.drawOnCanvas.bind(this);
    this.isHover = this.isHover.bind(this);
  }

  isHover(moreProps) {
    const {
      mouseXY,
      currentItem,
      chartConfig: { yScale },
    } = moreProps;
    const { yAccessor } = this.props;

    const node = yAccessor ? yAccessor(currentItem) : undefined;
    const value = node?.value;
    if (!isDefined(value)) return false;

    const y = mouseXY[1];
    const currentY = yScale(value);
    const tolerance = 6;
    return y < currentY + tolerance && y > currentY - tolerance;
  }

  drawOnCanvas(ctx, moreProps) {
    const { plotType, fillRising, fillFalling, highlightOnHover } = this.props;
    const {
      chartConfig: { yScale },
      plotData,
      xScale,
      xAccessor,
      hovering,
    } = moreProps;
    const { yAccessor } = this.props;

    if (!plotData || plotData.length === 0) return;

    const risingStroke = normalizeStroke(fillRising, {
      color: "#00FF00",
      width: 1,
      opacity: 0.9,
      dash: "Solid",
    });
    const fallingStroke = normalizeStroke(fillFalling, {
      color: "#FF0000",
      width: 1,
      opacity: 0.9,
      dash: "Solid",
    });

    const drawPoint = (x, y, stroke) => {
      const radius = Math.max(
        2,
        Math.min(
          4,
          stroke.width * 1.5 + (hovering && highlightOnHover ? 1 : 0),
        ),
      );
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, 2 * Math.PI, false);
      ctx.fillStyle = hexToRGBA(stroke.color, stroke.opacity ?? 1);
      ctx.strokeStyle = stroke.color;
      ctx.lineWidth = stroke.width;
      ctx.fill();
      ctx.stroke();
      ctx.closePath();
    };

    const drawSquareWave = (xLeft, xRight, y, stroke, dashed) => {
      ctx.beginPath();
      ctx.moveTo(xLeft, y);
      ctx.lineTo(xRight, y);
      ctx.lineWidth =
        hovering && highlightOnHover ? stroke.width + 1 : stroke.width;
      ctx.strokeStyle = stroke.color;
      ctx.globalAlpha = stroke.opacity ?? 1;
      ctx.setLineDash(dashed);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.globalAlpha = 1;
      ctx.closePath();
    };

    plotData.forEach((datum, index) => {
      const node = yAccessor ? yAccessor(datum) : undefined;
      if (!node) return;

      const { buy, sell, value, trend } = node;
      const activeValue = isDefined(value)
        ? value
        : isDefined(buy)
          ? buy
          : sell;
      if (!isDefined(activeValue)) return;

      const x = xScale(xAccessor(datum));
      const y = yScale(activeValue);
      if (!Number.isFinite(x) || !Number.isFinite(y)) return;

      const halfWidth = getBarWidth(plotData, xScale, xAccessor, index) / 2;
      const stroke =
        trend === "buy" || isDefined(buy) ? risingStroke : fallingStroke;
      const dashPattern = getStrokeDasharrayCanvas(stroke.dash);

      if (plotType === "squarewave") {
        const xLeft = x - halfWidth;
        const xRight = x + halfWidth;
        drawSquareWave(xLeft, xRight, y, stroke, dashPattern);

        const nextDatum = plotData[index + 1];
        const nextNode =
          nextDatum && yAccessor ? yAccessor(nextDatum) : undefined;
        if (
          nextNode &&
          isDefined(nextNode.value) &&
          nextNode.value !== activeValue
        ) {
          const nextX = nextDatum ? xScale(xAccessor(nextDatum)) : undefined;
          if (isDefined(nextX)) {
            ctx.beginPath();
            ctx.moveTo(xRight, y);
            ctx.lineTo(nextX - halfWidth, yScale(nextNode.value));
            ctx.lineWidth = stroke.width;
            ctx.strokeStyle = stroke.color;
            ctx.globalAlpha = stroke.opacity ?? 1;
            ctx.setLineDash(dashPattern);
            ctx.stroke();
            ctx.setLineDash([]);
            ctx.globalAlpha = 1;
            ctx.closePath();
          }
        }
      } else {
        drawPoint(x, y, stroke);
      }
    });
  }

  render() {
    const { highlightOnHover } = this.props;

    const hoverProps = highlightOnHover
      ? {
          isHover: this.isHover,
          drawOn: ["mousemove", "pan"],
          canvasToDraw: getMouseCanvas,
        }
      : {
          drawOn: ["pan"],
          canvasToDraw: getAxisCanvas,
        };

    return (
      <GenericChartComponent
        svgDraw={() => null}
        canvasDraw={this.drawOnCanvas}
        onClickWhenHover={this.props.onClick}
        onDoubleClickWhenHover={this.props.onDoubleClick}
        onContextMenuWhenHover={this.props.onContextMenu}
        {...hoverProps}
      />
    );
  }
}

ATRStopSeries.propTypes = {
  className: PropTypes.string,
  plotType: PropTypes.oneOf(["points", "squarewave"]),
  yAccessor: PropTypes.func.isRequired,
  fillRising: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  fillFalling: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  highlightOnHover: PropTypes.bool,
  onClick: PropTypes.func,
  onDoubleClick: PropTypes.func,
  onContextMenu: PropTypes.func,
};

ATRStopSeries.defaultProps = {
  className: "react-stockcharts-atr-stop",
  plotType: "points",
  fillRising: { color: "#00FF00", width: 1, opacity: 0.9, dash: "Solid" },
  fillFalling: { color: "#FF0000", width: 1, opacity: 0.9, dash: "Solid" },
  highlightOnHover: true,
  onClick: () => {},
  onDoubleClick: () => {},
  onContextMenu: () => {},
};

export default ATRStopSeries;
